'use client';

/**
 * TestCaseDetails - Operational Intelligence Card for test cases
 *
 * Transforms test results from "CI reports" into **Live Evidence Galleries**.
 *
 * Sections:
 * 1. Header - Name, category, case_id
 * 2. Business Context - User outcome, business value
 * 3. Latest Result - Summary with Evidence Gallery (products, POs, etc.)
 * 4. The Trace - Step-by-step execution anatomy
 * 5. Graph Health - Pass rate, avg latency from historical data
 *
 * @component_role Test Case Operational Intelligence
 * @business_rule Shows proof the system works, not just pass/fail
 * @user_outcome Auditors see actual products/data returned, not just status
 * @integration_point klyde_edge /testing/cases, /testing/history
 *
 * @layer presentation
 * @arch_component_type widget
 * @arch_domain testing
 */

import { useState, useEffect } from 'react';
import {
  FlaskConical,
  Loader2,
  AlertCircle,
  Briefcase,
  Link2,
  CheckCircle2,
  XCircle,
  ChevronDown,
  ChevronRight,
  Image as ImageIcon,
} from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils/cn';
import { api } from '@/lib/api/client';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface CaseInfo {
  case_id: string;
  name: string;
  description: string;
  category: string;
  user_outcome: string;
  business_value: string;
  apis_tested: string[];
}

interface CaseStats {
  semantic_id: string;
  case_id: string;
  name: string;
  category: string;
  total_runs: number;
  passes: number;
  failures: number;
  pass_rate: number | null;
  avg_duration_ms: number | null;
  last_run: string | null;
}

interface HistoryRecord {
  run_id: string;
  run_name: string;
  timestamp: string;
  trigger: string;
  environment: string;
  success: boolean;
  duration_ms: number;
  summary: string;
  details: string; // JSON string
}

interface ProductItem {
  semantic_id: string;
  name: string;
  entity_type: string;
  similarity?: number;
  image?: string;
  price?: number;
  vendor?: string;
}

interface StepInfo {
  step_name: string;
  method: string;
  endpoint: string;
  request_body?: Record<string, unknown> | null;
  status_code?: number | null;
  response_summary?: string | null;
  duration_ms?: number | null;
  success: boolean;
}

interface ParsedDetails {
  products?: ProductItem[];
  product_count?: number;
  conversation_id?: string;
  turn?: number;
  steps?: StepInfo[];
  [key: string]: unknown;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

interface TestCaseDetailsProps {
  nodeId: string;
  projectId?: string | null;
}

export function TestCaseDetails({ nodeId }: TestCaseDetailsProps) {
  const [caseInfo, setCaseInfo] = useState<CaseInfo | null>(null);
  const [stats, setStats] = useState<CaseStats | null>(null);
  const [latestRun, setLatestRun] = useState<HistoryRecord | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [traceExpanded, setTraceExpanded] = useState(true);
  const [payloadExpanded, setPayloadExpanded] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true);
      setError(null);
      try {
        // Fetch case info, stats, and latest run in parallel
        const [caseData, statsData, historyData] = await Promise.all([
          api.get<CaseInfo>(`/testing/cases/${nodeId}`),
          api.get<CaseStats>(`/testing/history/${nodeId}/stats`).catch(() => null),
          api.get<HistoryRecord[]>(`/testing/history/${nodeId}?limit=1`).catch(() => []),
        ]);
        setCaseInfo(caseData);
        setStats(statsData);
        setLatestRun(historyData?.[0] || null);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load test case');
      } finally {
        setIsLoading(false);
      }
    };

    if (nodeId) {
      fetchData();
    }
  }, [nodeId]);

  // Parse details JSON from latest run
  const parsedDetails: ParsedDetails | null = latestRun
    ? (() => {
        try {
          return JSON.parse(latestRun.details);
        } catch {
          return null;
        }
      })()
    : null;

  // Loading state
  if (isLoading) {
    return (
      <div className="flex items-center gap-2 text-sm text-muted-foreground p-4">
        <Loader2 className="h-4 w-4 animate-spin" />
        Loading test case...
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="flex items-center gap-2 text-sm text-destructive p-4">
        <AlertCircle className="h-4 w-4" />
        {error}
      </div>
    );
  }

  // No data
  if (!caseInfo) {
    return (
      <div className="text-sm text-muted-foreground p-4">
        Test case details not available
      </div>
    );
  }

  return (
    <div className="space-y-4 p-4">
      {/* ================================================================== */}
      {/* HEADER */}
      {/* ================================================================== */}
      <div className="flex items-start gap-3">
        <div className="p-2 rounded-lg bg-purple-500/10 border border-purple-500/20">
          <FlaskConical className="w-5 h-5 text-purple-400" />
        </div>
        <div className="flex-1 min-w-0">
          <h3 className="font-semibold text-sm">{caseInfo.name}</h3>
          <div className="flex items-center gap-2 mt-1">
            <Badge variant="secondary" className="text-[10px]">
              {caseInfo.category}
            </Badge>
            <span className="text-[10px] text-muted-foreground font-mono">
              {caseInfo.case_id}
            </span>
          </div>
          {stats?.semantic_id && (
            <span className="text-[9px] text-muted-foreground/60 font-mono mt-0.5 block">
              {stats.semantic_id}
            </span>
          )}
        </div>
        {latestRun && (
          <Badge
            variant="outline"
            className={cn(
              'text-[9px] shrink-0',
              latestRun.success
                ? 'border-green-500/30 text-green-600 dark:text-green-400'
                : 'border-red-500/30 text-red-600 dark:text-red-400'
            )}
          >
            {latestRun.success ? 'PASS' : 'FAIL'}
          </Badge>
        )}
      </div>

      {/* User Outcome (primary business context) */}
      <div className="text-sm text-muted-foreground italic border-l-2 border-purple-500/30 pl-3">
        "{caseInfo.user_outcome}"
      </div>

      {/* ================================================================== */}
      {/* LATEST RESULT */}
      {/* ================================================================== */}
      {latestRun && (
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <h4 className="text-xs font-semibold text-muted-foreground">LATEST RESULT</h4>
              <Badge variant="outline" className="text-[9px]">
                {latestRun.trigger}
              </Badge>
              <span className="text-[10px] text-muted-foreground">
                {formatTime(latestRun.timestamp)}
              </span>
            </div>
            <span className="text-[10px] font-mono text-muted-foreground">
              {latestRun.run_id}
            </span>
          </div>

          {/* Summary Banner */}
          <div
            className={cn(
              'rounded-md px-3 py-2 text-sm flex items-center gap-2',
              latestRun.success
                ? 'bg-green-500/10 border border-green-500/20 text-green-700 dark:text-green-400'
                : 'bg-red-500/10 border border-red-500/20 text-red-700 dark:text-red-400'
            )}
          >
            {latestRun.success ? (
              <CheckCircle2 className="w-4 h-4 shrink-0" />
            ) : (
              <XCircle className="w-4 h-4 shrink-0" />
            )}
            <span className="flex-1">{latestRun.summary}</span>
            <span className="text-[10px] opacity-70">
              {Math.round(latestRun.duration_ms)}ms
            </span>
          </div>

          {/* ============================================================== */}
          {/* EVIDENCE GALLERY */}
          {/* ============================================================== */}
          {parsedDetails?.products && parsedDetails.products.length > 0 && (
            <EvidenceGallery
              products={parsedDetails.products}
              productCount={parsedDetails.product_count}
              conversationId={parsedDetails.conversation_id}
              turn={parsedDetails.turn}
            />
          )}

          {/* ============================================================== */}
          {/* THE TRACE */}
          {/* ============================================================== */}
          {parsedDetails?.steps && parsedDetails.steps.length > 0 && (
            <div>
              <button
                onClick={() => setTraceExpanded(!traceExpanded)}
                className="flex items-center gap-2 text-xs font-semibold text-muted-foreground hover:text-foreground w-full"
              >
                {traceExpanded ? (
                  <ChevronDown className="w-3 h-3" />
                ) : (
                  <ChevronRight className="w-3 h-3" />
                )}
                THE TRACE ({parsedDetails.steps.length} steps)
              </button>
              {traceExpanded && (
                <div className="mt-2 space-y-1">
                  {parsedDetails.steps.map((step, i) => (
                    <StepRow key={i} step={step} index={i + 1} />
                  ))}
                </div>
              )}
            </div>
          )}

          {/* ============================================================== */}
          {/* INSPECT PAYLOAD (raw details) */}
          {/* ============================================================== */}
          <div>
            <button
              onClick={() => setPayloadExpanded(!payloadExpanded)}
              className="flex items-center gap-2 text-xs font-semibold text-muted-foreground hover:text-foreground w-full"
            >
              {payloadExpanded ? (
                <ChevronDown className="w-3 h-3" />
              ) : (
                <ChevronRight className="w-3 h-3" />
              )}
              INSPECT PAYLOAD
            </button>
            {payloadExpanded && parsedDetails && (
              <pre className="mt-2 p-2 bg-muted/50 rounded text-[10px] overflow-auto max-h-48 font-mono">
                {JSON.stringify(parsedDetails, null, 2)}
              </pre>
            )}
          </div>
        </div>
      )}

      {/* No runs yet */}
      {!latestRun && (
        <div className="text-sm text-muted-foreground text-center py-4">
          No runs yet. Click ▶ to execute.
        </div>
      )}

      {/* ================================================================== */}
      {/* BUSINESS CONTEXT (collapsible) */}
      {/* ================================================================== */}
      <div className="border-t border-border pt-3 space-y-2">
        <Section icon={Briefcase} title="BUSINESS VALUE">
          <p className="text-xs text-muted-foreground">{caseInfo.business_value}</p>
        </Section>

        {caseInfo.apis_tested.length > 0 && (
          <Section icon={Link2} title="APIs TESTED">
            <div className="flex flex-wrap gap-1">
              {caseInfo.apis_tested.map((apiPath, i) => (
                <Badge key={i} variant="outline" className="text-[9px] font-mono">
                  {apiPath}
                </Badge>
              ))}
            </div>
          </Section>
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Evidence Gallery - Products
// ---------------------------------------------------------------------------

interface EvidenceGalleryProps {
  products: ProductItem[];
  productCount?: number;
  conversationId?: string;
  turn?: number;
}

function EvidenceGallery({ products, productCount, conversationId, turn }: EvidenceGalleryProps) {
  return (
    <div>
      <div className="flex items-center justify-between mb-2">
        <h4 className="text-xs font-semibold text-muted-foreground">
          EVIDENCE ({productCount ?? products.length} products)
        </h4>
        {conversationId && (
          <span className="text-[9px] font-mono text-muted-foreground">
            {conversationId} · Turn {turn ?? 1}
          </span>
        )}
      </div>
      <div className="flex gap-2 overflow-x-auto pb-2">
        {products.slice(0, 8).map((p, i) => (
          <ProductCard key={p.semantic_id || i} product={p} />
        ))}
        {products.length > 8 && (
          <div className="flex-shrink-0 w-20 h-24 rounded border border-dashed border-muted-foreground/30 flex items-center justify-center text-xs text-muted-foreground">
            +{products.length - 8} more
          </div>
        )}
      </div>
    </div>
  );
}

function ProductCard({ product }: { product: ProductItem }) {
  return (
    <div className="flex-shrink-0 w-20 rounded border border-border bg-card overflow-hidden">
      {/* Image */}
      <div className="h-14 bg-muted flex items-center justify-center">
        {product.image ? (
          <img
            src={product.image}
            alt={product.name}
            className="w-full h-full object-cover"
            onError={(e) => {
              (e.target as HTMLImageElement).style.display = 'none';
            }}
          />
        ) : (
          <ImageIcon className="w-5 h-5 text-muted-foreground/50" />
        )}
      </div>
      {/* Info */}
      <div className="p-1.5">
        <p className="text-[9px] font-medium truncate" title={product.name}>
          {product.name}
        </p>
        <div className="flex items-center justify-between mt-0.5">
          {product.price != null && (
            <span className="text-[9px] text-green-600 dark:text-green-400 font-medium">
              ${product.price.toLocaleString()}
            </span>
          )}
          {product.similarity != null && (
            <span className="text-[8px] text-muted-foreground">
              {Math.round(product.similarity * 100)}%
            </span>
          )}
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Step Row (The Trace) - Shows request/response details
// ---------------------------------------------------------------------------

function StepRow({ step, index }: { step: StepInfo; index: number }) {
  return (
    <div
      className={cn(
        'rounded border text-xs mb-2',
        step.success
          ? 'bg-green-500/5 border-green-500/20'
          : 'bg-red-500/5 border-red-500/20'
      )}
    >
      {/* Header row */}
      <div className="flex items-center gap-2 px-2 py-1.5 border-b border-inherit">
        <span
          className={cn(
            'w-4 h-4 rounded-full flex items-center justify-center text-[9px] font-bold shrink-0',
            step.success
              ? 'bg-green-500/20 text-green-600 dark:text-green-400'
              : 'bg-red-500/20 text-red-600 dark:text-red-400'
          )}
        >
          {index}
        </span>
        <span className="font-medium">{step.step_name}</span>
        <span className="flex-1" />
        {step.status_code && (
          <span
            className={cn(
              'text-[9px] font-mono font-bold',
              step.status_code < 400 ? 'text-green-600' : 'text-red-600'
            )}
          >
            {step.status_code}
          </span>
        )}
        {step.duration_ms != null && (
          <span className="text-[9px] text-muted-foreground">{Math.round(step.duration_ms)}ms</span>
        )}
      </div>

      {/* Request/Response details */}
      <div className="px-2 py-1.5 space-y-1">
        {/* Request */}
        <div className="flex items-start gap-2">
          <span className="text-[9px] text-muted-foreground w-8 shrink-0">REQ</span>
          <div className="flex-1 min-w-0">
            <code className="text-[10px] font-mono text-blue-600 dark:text-blue-400">
              {step.method} {step.endpoint}
            </code>
            {step.request_body && Object.keys(step.request_body).length > 0 && (
              <div className="mt-1 p-1.5 bg-muted/50 rounded text-[9px] font-mono break-all">
                {formatRequestBody(step.request_body)}
              </div>
            )}
          </div>
        </div>

        {/* Response */}
        {step.response_summary && (
          <div className="flex items-start gap-2">
            <span className="text-[9px] text-muted-foreground w-8 shrink-0">RES</span>
            <span className="text-[10px] text-muted-foreground">{step.response_summary}</span>
          </div>
        )}
      </div>
    </div>
  );
}

/**
 * Format request body for display.
 * Shows key fields inline, truncates long values.
 */
function formatRequestBody(body: Record<string, unknown>): React.ReactNode {
  const entries = Object.entries(body);

  return (
    <div className="space-y-0.5">
      {entries.map(([key, value]) => (
        <div key={key} className="flex gap-1">
          <span className="text-muted-foreground">{key}:</span>
          <span className="text-foreground truncate">
            {formatValue(value)}
          </span>
        </div>
      ))}
    </div>
  );
}

function formatValue(value: unknown): string {
  if (value === null || value === undefined) return 'null';
  if (typeof value === 'string') {
    // Truncate long strings (like base64 images)
    if (value.length > 50) {
      return `"${value.slice(0, 47)}..."`;
    }
    return `"${value}"`;
  }
  if (typeof value === 'number' || typeof value === 'boolean') {
    return String(value);
  }
  if (Array.isArray(value)) {
    return `[${value.length} items]`;
  }
  if (typeof value === 'object') {
    return JSON.stringify(value);
  }
  return String(value);
}

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

function Section({
  icon: Icon,
  title,
  children,
}: {
  icon: React.ComponentType<{ className?: string }>;
  title: string;
  children: React.ReactNode;
}) {
  return (
    <div>
      <div className="flex items-center gap-2 mb-1">
        <Icon className="w-3 h-3 text-muted-foreground" />
        <h4 className="text-[10px] font-semibold text-muted-foreground">{title}</h4>
      </div>
      {children}
    </div>
  );
}


// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function formatTime(iso: string): string {
  try {
    const d = new Date(iso);
    return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  } catch {
    return iso;
  }
}
